import React, { memo, useState, useCallback } from "react"
import { Field, FieldProps } from "react-final-form"
import { FieldValidator } from "final-form"

import { DefaultField, DefaultFieldProps, getValidator } from "components/form"
import Select from "components/form/select"

import { Option, OptionsGroup, Options } from "typings/select-options"

interface SelectWrapperProps {
  onChange: (arg: any) => void
  placeholder?: string
  value: string
  options: Options<string>
  disabled?: boolean
  error?: boolean
  handleResetError?: () => void
}

const SelectWrapper: React.FC<SelectWrapperProps> = props => {
  const { value, options, disabled, onChange, error, placeholder, handleResetError } = props

  let flatOptions: Option<string>[] = options as Option<string>[]
  let selectedOption: Option<string> | undefined = undefined

  /**
   * Property 'option' can be either an array of options or an array of grouped options
   * so we may need to flatten the array of possible options
   */
  if (options.length !== 0 && !(options[0] as Option<string>).value) {
    flatOptions = (options as OptionsGroup<string>[]).map(optionGroup => optionGroup.options).flat()
  }

  /**
   * Map 'value' property back to an option for react-select
   */
  selectedOption = flatOptions.find(option => option.value === value)

  const handleChange = (value: React.ChangeEvent<HTMLSelectElement>) => {
    error && handleResetError && handleResetError()
    onChange(value)
  }

  return (
    <Select
      value={value}
      error={error}
      options={options}
      disabled={disabled}
      onChange={handleChange}
      placeholder={placeholder}
      isSearchable
      selectedOptions={selectedOption}
    />
  )
}

export const DropdownInputField: React.FC<FieldProps<number, HTMLSelectElement>> = props => {
  const [error, setError] = useState<boolean>(false)

  const handleSetError = useCallback(() => setError(true), [])
  const handleResetError = useCallback(() => setError(false), [])

  let validate: FieldValidator<number> = () => null
  validate = getValidator(validate, props.required)

  return (
    <Field
      InputComponent={SelectWrapper}
      parse={optionOrValue => optionOrValue.value || optionOrValue}
      px={0}
      py={0}
      {...props}
      validate={validate}
      data-test={props.dataTest}
    >
      {(inputProps: DefaultFieldProps<number>) => (
        <DefaultField
          {...inputProps}
          handleResetError={handleResetError}
          handleSetError={handleSetError}
          error={error}
          border="none !important"
        />
      )}
    </Field>
  )
}

export default memo(DropdownInputField)
